import { useFormik } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import { useMutation } from "@tanstack/react-query";
import { post } from "../../../api/util";
import { PersonDto } from "../../../interfaces/schema";
import { Dispatch, SetStateAction } from "react";
import { dateIsWithinInterval } from "../../../util/date";

export function useDeponerAdvokat(
  person: PersonDto,
  setReceiptHeading: Dispatch<SetStateAction<string>>,
) {
  const formik = useFormik<DeponerAdvokatFormik>({
    validateOnMount: true,
    initialValues: {
      skalDeponeres: true,
      skalFrakendes: false,
      fraDato: undefined,
      aarsag: "",
    },
    validationSchema: Yup.object({
      skalDeponeres: Yup.boolean(),
      skalFrakendes: Yup.boolean(),
      fraDato: Yup.date().nullable().required("Dato skal være sat"),
      aarsag: Yup.string().when("skalFrakendes", {
        is: true,
        then: (schema) => schema.required("Der skal oplyses en årsag"),
      }),
    }),
    onSubmit: (values) => {
      const { aarsag, skalDeponeres, fraDato } = values;
      setReceiptHeading(
        skalDeponeres ? "Deponering gennemført" : "Frakendelse gennemført",
      );
      const aktiveFirmaer = person.personFirma?.filter((pf) =>
        fraDato
          ? dateIsWithinInterval(
              fraDato,
              new Date(pf.periode.fraDato),
              new Date(pf.periode.tilDatoInklusiv),
            )
          : false,
      );
      const futureFirmaRelationer =
        fraDato && person.personFirma
          ? person.personFirma.filter(
              (element) => new Date(element?.periode.fraDato) > fraDato,
            )
          : [];
      const dto: AfslutBeskikkelseDTO = {
        type: skalDeponeres ? "Deponering" : "Frakendelse",
        fraDato: fraDato ? format(fraDato, "yyyy-MM-dd") : "",
        aarsag,
        fortsaettendePersonFirmarelationer: futureFirmaRelationer.map(
          (f) => f.id,
        ),
        ophoerendePersonFirmarelationer: aktiveFirmaer?.map((f) => f.id) ?? [],
      };
      afslutBeskikkelseQuery.mutate(dto);
    },
  });
  const afslutBeskikkelseQuery = useMutation({
    mutationFn: (dto: AfslutBeskikkelseDTO) => {
      return post<{ id: string; fornavne: string; efternavn: string }>({
        query: `person/${person.id}/wizards/afslutbeskikkelse`,
        body: dto,
      });
    },
  });
  return { formik, afslutBeskikkelseQuery };
}

export type DeponerAdvokatFormik = {
  skalDeponeres: boolean;
  skalFrakendes: boolean;
  fraDato: Date | undefined;
  aarsag: string;
};

interface AfslutBeskikkelseDTO {
  type: "Deponering" | "Frakendelse";
  fraDato: string;
  aarsag: string;
  ophoerendePersonFirmarelationer: string[];
  fortsaettendePersonFirmarelationer: string[];
}
